"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, ArrowUpRight, ChevronDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandLogo } from "./BrandLogo";
import { PRODUCT_LINE } from "@/lib/products";

/** The bar's plain links. Product sits before them as a dropdown. */
const NAV = [
  { href: "/solutions", label: "Solutions" },
  { href: "/pricing", label: "Pricing" },
  { href: "/clinical-trials", label: "Clinical Trials Live" },
];

/** The products group at the top of the grid menu: the same list as the dropdown. */
const PRODUCTS = PRODUCT_LINE.map((p) => ({ href: p.href, label: p.name, external: !!p.external }));

/**
 * The rest of the grid menu: the header links that are not already in the
 * products group, then everything else worth a top-level link.
 */
const MENU = [
  ...NAV.filter((n) => !PRODUCTS.some((p) => p.href === n.href)),
  { href: "/platform", label: "Platform" },
  { href: "/compliance", label: "Compliance" },
  { href: "/about", label: "About" },
  { href: "/demo", label: "Contact" },
];

/** Pages that open with a photo hero, so the glass header starts clear with white type. None today. */
const HERO_PATHS: string[] = [];

/**
 * Floating glass header, inset from the page edges. Over a photo hero it is
 * clear glass with white type; once the photo has scrolled away (and on pages
 * without one) it turns to frosted white with dark type.
 */
export function SiteHeader() {
  const pathname = usePathname();
  const photo = HERO_PATHS.includes(pathname);
  const [onHero, setOnHero] = useState(photo);
  const [menuOpen, setMenuOpen] = useState(false);
  // The products group starts open on a product page, closed elsewhere.
  const [productsOpen, setProductsOpen] = useState(pathname.startsWith("/platform") || pathname.startsWith("/product") || pathname.startsWith("/edc"));
  // The Product dropdown in the bar: opens on hover or click, closes on a
  // click outside it, on Escape and on navigation.
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setDropOpen(false);
    if (!photo) {
      setOnHero(false);
      return;
    }
    // One rect read per scroll event is cheap; React skips the render when the value has not changed.
    const update = () => {
      const hero = document.getElementById("hero");
      setOnHero(!!hero && hero.getBoundingClientRect().bottom > 90);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [pathname, photo]);

  // Escape closes the menu wherever focus is.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setDropOpen(false);
      }
    };
    const onDown = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, []);

  const isActive = (href: string) => (href === "/" ? pathname === "/" : pathname.startsWith(href));

  return (
    <>
      {menuOpen && <div className="gnav-scrim" onClick={() => setMenuOpen(false)} />}
      <header className={cn("gnav", onHero && "gnav-hero", menuOpen && "gnav-open")}>
        <div className="gnav-bar">
          <Link href="/" className="gnav-logo" aria-label="Trialion home">
            <BrandLogo />
          </Link>
          <nav className="gnav-links" aria-label="Main">
            <div
              ref={dropRef}
              className="gnav-drop"
              onMouseEnter={() => setDropOpen(true)}
              onMouseLeave={() => setDropOpen(false)}
            >
              <button
                type="button"
                className={cn(
                  "gnav-drop-btn",
                  (isActive("/product") || isActive("/platform") || isActive("/edc")) && "active",
                  dropOpen && "open",
                )}
                aria-expanded={dropOpen}
                aria-controls="product-menu"
                onClick={() => setDropOpen((o) => !o)}
              >
                Product
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </button>
              <div id="product-menu" className={cn("gnav-drop-menu", dropOpen && "open")}>
                <ul>
                  {PRODUCT_LINE.map((p) => {
                    const body = (
                      <>
                        <span className="gnav-drop-name">
                          {p.name}
                          {p.status !== "Live" && <span className="gnav-drop-tag">{p.status}</span>}
                          {p.external && <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />}
                        </span>
                        <span className="gnav-drop-short">{p.short}</span>
                      </>
                    );
                    return (
                      <li key={p.slug}>
                        {p.external ? (
                          <a href={p.href} target="_blank" rel="noopener noreferrer" tabIndex={dropOpen ? 0 : -1}>
                            {body}
                          </a>
                        ) : (
                          <Link href={p.href} tabIndex={dropOpen ? 0 : -1} onClick={() => setDropOpen(false)}>
                            {body}
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <Link className="gnav-drop-all" href="/product" tabIndex={dropOpen ? 0 : -1} onClick={() => setDropOpen(false)}>
                  All products
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
            {NAV.map((n) => (
              <Link key={n.href} href={n.href} className={cn(isActive(n.href) && "active")}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="gnav-actions">
            <Link className="gnav-pill" href="/demo">
              Book a demo
            </Link>
            <button
              type="button"
              className="gnav-grid"
              aria-expanded={menuOpen}
              aria-controls="site-menu"
              aria-label="Menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span className="dots" aria-hidden="true">
                {Array.from({ length: 9 }, (_, i) => (
                  <i key={i} />
                ))}
              </span>
            </button>
          </div>
        </div>
        <div id="site-menu" className="gnav-menu">
          <button
            type="button"
            className={cn(
              "menu-link menu-parent",
              productsOpen && "open",
              (pathname.startsWith("/platform") || pathname.startsWith("/product") || pathname.startsWith("/edc")) && "active",
            )}
            aria-expanded={productsOpen}
            aria-controls="menu-products"
            onClick={() => setProductsOpen((o) => !o)}
            tabIndex={menuOpen ? 0 : -1}
          >
            Products
            <Plus className="menu-plus h-[18px] w-[18px]" />
          </button>
          <div id="menu-products" className={cn("menu-sub", productsOpen && "open")}>
            <div className="menu-sub-inner">
              {PRODUCTS.map((p) =>
                p.external ? (
                  <a
                    key={p.href}
                    className="menu-sublink"
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={menuOpen && productsOpen ? 0 : -1}
                  >
                    {p.label}
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                ) : (
                  <Link
                    key={p.href}
                    className={cn("menu-sublink", isActive(p.href) && "active")}
                    href={p.href}
                    tabIndex={menuOpen && productsOpen ? 0 : -1}
                  >
                    {p.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ),
              )}
            </div>
          </div>
          {MENU.map((m) => (
            <Link
              key={m.href}
              className={cn("menu-link", isActive(m.href) && "active")}
              href={m.href}
              tabIndex={menuOpen ? 0 : -1}
            >
              {m.label}
              <ArrowRight className="h-[18px] w-[18px]" />
            </Link>
          ))}
          <Link
            className="btn-grad menu-cta inline-flex items-center justify-center font-semibold"
            href="/demo"
            tabIndex={menuOpen ? 0 : -1}
          >
            Book a demo
          </Link>
        </div>
      </header>
    </>
  );
}
